import ArrowDownward from '@mui/icons-material/ArrowDownward';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import Remove from '@mui/icons-material/Remove';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

export interface MetricsCellProps {
  value: number | null | undefined;
  /** Deals List landing delta as a percentage (23.2 = +23.2%), already rounded by the backend. Not a fraction. */
  delta: number | null | undefined;
  format: 'currency' | 'number' | 'cpm';
}

const valueFormatter: Record<MetricsCellProps['format'], (value: number) => string> = {
  currency: value =>
    new Intl.NumberFormat('en-US', {
      currency: 'USD',
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
      style: 'currency',
    }).format(value),
  number: value => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value),
  cpm: value =>
    new Intl.NumberFormat('en-US', {
      currency: 'USD',
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
      style: 'currency',
    }).format(value),
};

// Backend already rounds delta to 1 decimal; this pads to 1 digit and caps float noise at 2 digits.
const deltaFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 2,
  minimumFractionDigits: 1,
  signDisplay: 'exceptZero',
});

const formatDelta = (delta: number) => `${deltaFormatter.format(delta)}%`;

export const MetricsCell = ({ value, delta, format }: MetricsCellProps) => {
  if (value === null || value === undefined) {
    return <Typography sx={{ color: 'text.disabled' }}>–</Typography>;
  }

  const formattedValue = valueFormatter[format](value);
  const numericDelta = Number(delta);
  const hasDelta = Number.isFinite(numericDelta) && numericDelta !== 0;

  const showPositive = hasDelta && numericDelta > 0;
  const showNegative = hasDelta && numericDelta < 0;
  const showNeutral = !hasDelta;
  const tooltipText = hasDelta ? formatDelta(numericDelta) : '';

  const icon = showPositive ? (
    <ArrowUpward sx={{ color: 'success.main', fontSize: 14 }} />
  ) : showNegative ? (
    <ArrowDownward sx={{ color: 'error.main', fontSize: 14 }} />
  ) : (
    <Remove sx={{ color: 'text.disabled', fontSize: 14 }} />
  );

  const deltaContent = showNeutral ? (
    icon
  ) : (
    <Tooltip title={tooltipText} placement="top">
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
        {icon}
      </Box>
    </Tooltip>
  );

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
        {formattedValue}
      </Typography>
      {deltaContent}
    </Box>
  );
};
